import {
  CheckCircle2,
  CircleDashed,
  Clock,
  Eye,
  MessageCircle,
  Package,
  ShieldAlert,
  Sparkles,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import type { ContactCategory, ContactMessageStatus } from './types'

export interface StatusDescriptor {
  label: string
  blurb: string
  icon: LucideIcon
  badgeVariant: 'default' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'outline' | 'brand'
  /** Lifecycle order for the timeline. Lower = earlier. */
  order: number
  /** Whether this is a terminal state. */
  terminal: boolean
}

/**
 * Status vocabulary is shared across all four categories so the schema
 * stays simple, but the labels we show the user are tailored per category
 * (e.g. "shipped" reads as "Released" for a feature, "Fixed" for a bug,
 * "Deal closed" for sales, "Answered" for support).
 */
const COMMON: Record<Exclude<ContactMessageStatus, 'shipped' | 'wont_do' | 'planned' | 'already_available'>, StatusDescriptor> = {
  requested: {
    label: 'Requested',
    blurb: 'Submitted to Bokapsys.',
    icon: CircleDashed,
    badgeVariant: 'outline',
    order: 1,
    terminal: false,
  },
  viewed: {
    label: 'Viewed by Bokapsys team',
    blurb: 'The team has seen your message and is reviewing it.',
    icon: Eye,
    badgeVariant: 'info',
    order: 2,
    terminal: false,
  },
  in_progress: {
    label: 'In progress',
    blurb: 'The team is working on it. Reply below if anything changes.',
    icon: MessageCircle,
    badgeVariant: 'warning',
    order: 3,
    terminal: false,
  },
  closed: {
    label: 'Closed',
    blurb: 'No further action expected.',
    icon: XCircle,
    badgeVariant: 'secondary',
    order: 5,
    terminal: true,
  },
}

const PER_CATEGORY: Record<ContactCategory, Partial<Record<ContactMessageStatus, StatusDescriptor>>> = {
  sales: {
    planned: {
      label: 'In active discussion',
      blurb: 'A Bokapsys rep is engaged with you on this lead.',
      icon: Sparkles,
      badgeVariant: 'info',
      order: 4,
      terminal: false,
    },
    already_available: {
      label: 'Already covered',
      blurb: 'Your needs are already met by an existing plan.',
      icon: Package,
      badgeVariant: 'secondary',
      order: 4,
      terminal: true,
    },
    shipped: {
      label: 'Deal closed',
      blurb: 'This lead has been won.',
      icon: CheckCircle2,
      badgeVariant: 'success',
      order: 4,
      terminal: true,
    },
    wont_do: {
      label: 'Not a fit',
      blurb: 'Bokapsys was unable to meet the requirements on this one.',
      icon: ShieldAlert,
      badgeVariant: 'danger',
      order: 4,
      terminal: true,
    },
  },
  bug: {
    planned: {
      label: 'Fix planned',
      blurb: 'Confirmed and queued for a fix.',
      icon: Clock,
      badgeVariant: 'info',
      order: 4,
      terminal: false,
    },
    already_available: {
      label: 'Working as designed',
      blurb: 'This is the intended behaviour - see the note for context.',
      icon: Package,
      badgeVariant: 'secondary',
      order: 4,
      terminal: true,
    },
    shipped: {
      label: 'Fixed',
      blurb: 'The fix has been released.',
      icon: CheckCircle2,
      badgeVariant: 'success',
      order: 4,
      terminal: true,
    },
    wont_do: {
      label: 'Won’t fix',
      blurb: 'After investigation Bokapsys won’t change this. See the note.',
      icon: ShieldAlert,
      badgeVariant: 'danger',
      order: 4,
      terminal: true,
    },
  },
  feature: {
    planned: {
      label: 'Planned',
      blurb: 'Accepted into the roadmap. Target date below if set.',
      icon: Clock,
      badgeVariant: 'info',
      order: 4,
      terminal: false,
    },
    already_available: {
      label: 'Already in the app',
      blurb: 'The note below points to where this lives today.',
      icon: Package,
      badgeVariant: 'secondary',
      order: 4,
      terminal: true,
    },
    shipped: {
      label: 'Released',
      blurb: 'This feature has shipped - thanks for the request.',
      icon: CheckCircle2,
      badgeVariant: 'success',
      order: 4,
      terminal: true,
    },
    wont_do: {
      label: 'Won’t build',
      blurb: 'After review Bokapsys won’t take this on. See the note.',
      icon: ShieldAlert,
      badgeVariant: 'danger',
      order: 4,
      terminal: true,
    },
  },
  support: {
    planned: {
      label: 'Looking into it',
      blurb: 'A Bokapsys rep is researching your question.',
      icon: Clock,
      badgeVariant: 'info',
      order: 4,
      terminal: false,
    },
    already_available: {
      label: 'See note',
      blurb: 'Bokapsys has answered with a pointer in the note below.',
      icon: Package,
      badgeVariant: 'secondary',
      order: 4,
      terminal: true,
    },
    shipped: {
      label: 'Answered',
      blurb: 'Your question has been answered in the thread.',
      icon: CheckCircle2,
      badgeVariant: 'success',
      order: 4,
      terminal: true,
    },
    wont_do: {
      label: 'Out of scope',
      blurb: 'This question is outside what Bokapsys can answer.',
      icon: ShieldAlert,
      badgeVariant: 'danger',
      order: 4,
      terminal: true,
    },
  },
}

export function describeStatus(status: ContactMessageStatus, category: ContactCategory): StatusDescriptor {
  const tailored = PER_CATEGORY[category]?.[status]
  if (tailored) return tailored
  if (status in COMMON) {
    return COMMON[status as keyof typeof COMMON]
  }
  // Fallback shouldn’t hit because PER_CATEGORY covers terminal codes
  // and COMMON covers the lifecycle codes.
  return {
    label: status,
    blurb: '',
    icon: CircleDashed,
    badgeVariant: 'outline',
    order: 99,
    terminal: false,
  }
}

export const LIFECYCLE_ORDER: ContactMessageStatus[] = [
  'requested',
  'viewed',
  'in_progress',
]

export const TERMINAL_STATUSES: ContactMessageStatus[] = [
  'planned',
  'already_available',
  'shipped',
  'wont_do',
  'closed',
]
